/**
 * ENGAGE-PRICING-SPLIT-1 — split out of EngagementCreate.tsx (was 522 lines,
 * over the FILE_SIZE_POLICY 400-line hard stop; split-first was mandatory
 * before this file could be edited at all).
 *
 * Two things live here, kept apart from each other on purpose:
 *
 *  1. ENGAGE-SIZING-1's EBITDA-proxy sizing (revenue/deal-size -> EBITDA
 *     equivalent). Sizes the engagement only — never a reported figure.
 *
 *  2. The add-on registry (ADD_ONS). ENGAGE-PRICING-SPLIT-1 deliberately did
 *     NOT touch this: the dollar amounts are correct, only their *location*
 *     is wrong (client-computed instead of server-priced). Moving that is
 *     ENGAGE-ADDON-PRICING-1, tracked separately — see docs/OPEN_THREADS.md.
 *     Extending build_qoe_quote to price add-ons was considered and rejected
 *     for this pass: QuoteBuilder.tsx already consumes that response shape
 *     and works, and widening it here would be a Ring 3 change risking that
 *     working consumer to fix a defect that lives entirely on this screen.
 *
 * What is NOT here: base-fee calculation. assignTier / pricingBand /
 * pricingBandLabel / calculateBaseFee (the old flat-band, single-$3M-break
 * pricer that was actively wrong — $14,000 vs the server's correct $9,516 at
 * the EBITDA point that should have priced Standard) are deleted outright,
 * not moved here as a fallback. The base fee now comes from POST /qoe/quote
 * (build_qoe_quote, apps/api/routers/qoe/catalogue.py) via buildQuote() in
 * @/api/scope — the same server-authoritative call QuoteBuilder.tsx already
 * uses. See EngagementCreate.tsx for the wiring.
 */
import type { ScopeTier } from "@/lib/qoeCatalogue.generated";
import { money, fracPct } from "@/utils/format";

// ── Engagement sizing (ENGAGE-SIZING-1, unchanged) ─────────────────────────
//
// The tier bands are defined on EBITDA, but a CPA scoping a new engagement
// almost never has an adjusted EBITDA figure yet — that is the deliverable,
// not the input. What they do know on day one is annual revenue or the
// asking price. So the engagement is sized on whichever of those they have,
// converted to an EBITDA-equivalent by a lower-middle-market rule of thumb.
//
// These proxies size the engagement and nothing else. They never reach a
// report, a valuation, or any reported figure — the actual EBITDA is
// computed from the client's own statements in the normalization workspace.

export type SizingBasis = "revenue" | "deal_size";

// 12% EBITDA margin — a conservative blended lower-middle-market assumption.
export const REVENUE_EBITDA_MARGIN = 0.12;
// 5.0x EBITDA — the middle of the blended small-business multiple range.
export const DEAL_SIZE_EBITDA_MULTIPLE = 5.0;

export const SIZING_BASES: Record<SizingBasis, { label: string; field: string; help: string }> = {
  revenue: {
    label: "Annual revenue",
    field: "Annual Revenue (in $M)",
    help: `Sized at an assumed ${fracPct(REVENUE_EBITDA_MARGIN, 0)} EBITDA margin.`,
  },
  deal_size: {
    label: "Deal size (asking price / enterprise value)",
    field: "Deal Size (in $M)",
    help: `Sized at an assumed ${DEAL_SIZE_EBITDA_MULTIPLE.toFixed(1)}x EBITDA.`,
  },
};

export function sizingToEbitda(basis: SizingBasis, dollars: number): number {
  if (dollars <= 0) return 0;
  return basis === "revenue"
    ? dollars * REVENUE_EBITDA_MARGIN
    : dollars / DEAL_SIZE_EBITDA_MULTIPLE;
}

// ── Legacy pricing-band collapse ────────────────────────────────────────────
//
// POST /qoe/deals still speaks the 2-value pricing band (essentials |
// comprehensive) in its `tier` field, kept for back-compat — see
// core/engagements/pricing.py's EngagementTier / assign_tier(), which this
// mirrors exactly: lite -> essentials, focused/full -> comprehensive. Do not
// diverge from that collapse; it is the one place the mapping is defined and
// this is a second, necessary copy of it (Python enum, not importable here).

export function legacyPricingBand(tier: ScopeTier): "essentials" | "comprehensive" {
  return tier === "lite" ? "essentials" : "comprehensive";
}

// ── Tier ordering ────────────────────────────────────────────────────────
//
// Matches core/engagements/tiers.py's QOE registry order (lite < focused <
// full). Used only for the add-on minimum-tier gate below.

const TIER_ORDER: Record<ScopeTier, number> = { lite: 0, focused: 1, full: 2 };

export function tierAtLeast(tier: ScopeTier, min: ScopeTier): boolean {
  return TIER_ORDER[tier] >= TIER_ORDER[min];
}

// ── Add-on registry (ENGAGE-ADDON-PRICING-1: location, not amount, is the
// open gap — see file header) ───────────────────────────────────────────────

export interface AddOn {
  key: string;
  label: string;
  /** Minimum scope tier required to select this add-on (inclusive). */
  minTier?: ScopeTier;
  isPercent?: boolean;
  amount: number;
}

export const ADD_ONS: AddOn[] = [
  // ENGAGE-ADDON-PRICING-1 step 1 (owner-approved 2026-08-23): 30% -> 35%.
  { key: "expedite",          label: "Expedited delivery (+35%)",                         isPercent: true,  amount: 0.35 },
  { key: "additional_entity", label: "Additional entity (Essentials +$1,500 / Standard+ +$3,000)", amount: 0 },
  { key: "sellside_narrative",label: "Sell-side narrative",                                amount: 4_000 },
  { key: "roadmap_100day",    label: "100-day roadmap",                                    amount: 6_000 },
  // wc_trueup ("Working capital true-up", $3,500) removed 2026-08-23 —
  // ENGAGE-ADDON-PRICING-1 step 1: NWC is now core scope, not an add-on.
  // Previously gated on the legacy 2-value "comprehensive" band, which under
  // the old single-$3M-break auto-tier covered both today's focused and full
  // scope tiers. Gating on minTier: "focused" preserves that same population
  // rather than narrowing it to full-only as a side effect of widening the
  // tier vocabulary from 2 values to 3 — narrowing that population was never
  // asked for and isn't part of this fix.
  { key: "carveout",          label: "Carve-out analysis",                                minTier: "focused", amount: 5_000 },
  { key: "multicurrency",     label: "Multi-currency",                                    minTier: "focused", amount: 4_000 },
  // ENGAGE-ADDON-PRICING-1 step 1: three new add-ons, owner-approved 2026-08-23.
  { key: "bring_down_update", label: "Bring-down update (per quarter)",                    amount: 2_500 },
  { key: "state_nexus_study", label: "State nexus study",                                  amount: 4_000 },
  { key: "buyer_qa_support",  label: "Buyer Q&A support (block of hours)",                 amount: 2_500 },
];

// ENGAGE-ADDON-PRICING-1 step 2/3: the one place an add-on's *base* (catalogue)
// dollar amount is computed, shared by AddOnsCard (display) and
// EngagementCreate (total + payload) so the two can never drift. Overriding
// happens on top of this value, never in place of it — the override is
// compared against exactly this number to decide whether it "is" an override.
export function addOnBaseAmount(ao: AddOn, tier: ScopeTier, baseFee: number): number {
  if (ao.isPercent) return baseFee * ao.amount;
  if (ao.key === "additional_entity") return additionalEntityFee(tier);
  return ao.amount;
}

// Same collapse as legacyPricingBand: lite is Essentials, everything else
// (focused/full) is priced as the old "comprehensive" add-on fee. No new
// price point is introduced for the Standard tier specifically — the two
// constants that existed before (1,500 / 3,000) are the two that exist now.
export function additionalEntityFee(tier: ScopeTier): number {
  return tier === "lite" ? 1_500 : 3_000;
}

// ── Formatting helpers ──────────────────────────────────────────────────────

export function fmt(n: number): string {
  return money(n);
}

// Figures on this form are entered in millions. Anything unparseable or
// negative reads as "not provided" rather than as an error the CPA has to
// clear.
export function parseMillions(raw: string): number {
  const parsed = parseFloat(raw.replace(/[^0-9.]/g, ""));
  return isNaN(parsed) || parsed <= 0 ? 0 : parsed * 1_000_000;
}
